import { useCallback, useEffect, useRef } from "react";

export const useScrollToSection = () => {
	const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const observerRef = useRef<IntersectionObserver | null>(null);
	const onResultInViewRef = useRef<((id: string) => void) | undefined>();
	const pendingSectionsRef = useRef<string[]>([]);

	const registerSection = useCallback((id: string, element: HTMLDivElement | null) => {
		sectionRefs.current[id] = element;

		if (element && observerRef.current) {
			observerRef.current.observe(element);
		} else if (element) {
			pendingSectionsRef.current.push(id);
		}
	}, []);

	const jumpToSection = useCallback((sectionId: string) => {
		sectionRefs.current[sectionId]?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, []);

	const setupIntersectionObserver = useCallback((onResultInView?: (id: string) => void) => {
		onResultInViewRef.current = onResultInView;

		if (observerRef.current) {
			observerRef.current.disconnect();
		}

		if (!onResultInView) return () => {};

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

				if (visible?.target) {
					const id = (visible.target as HTMLElement).dataset.resultId!;
					onResultInView(id);
				}
			},
			{
				root: null,
				rootMargin: "0px 0px -30% 0px",
				threshold: [0.1, 0.3, 0.5, 0.7, 0.9],
			},
		);

		observerRef.current = observer;

		Object.values(sectionRefs.current).forEach((el) => {
			if (el) observer.observe(el);
		});

		pendingSectionsRef.current.forEach((id) => {
			const element = sectionRefs.current[id];
			if (element) observer.observe(element);
		});
		pendingSectionsRef.current = [];

		return () => {
			observer.disconnect();
			observerRef.current = null;
		};
	}, []);

	useEffect(() => {
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, []);

	return {
		registerSection,
		jumpToSection,
		setupIntersectionObserver,
	};
};
